'use client';

import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import type { NavItem } from '@/api/types/navigationTypes';
import { Icon } from '@/components/Icon/Icon';
import { isUserAuthenticated } from '@/features/auth/client';
import { cn } from '@/utils/ui';

const ICON = 'icon';
const TEXT_SUBSCRIPTION = 'text_subscription';

interface NavigationItemProps {
  type?: string;
  navItem: NavItem;
  className?: string;
  isLast?: boolean;
}

export function NavigationItem(props: NavigationItemProps): React.ReactNode {
  const { navItem, type = 'default', className, isLast = false } = props;

  const { item_key, display_name, view_type, images, visibility } = navItem;
  const { canonicalName, displayName, safeUrl } = navItem.parsedProps;
  const isAuthenticated = isUserAuthenticated();

  const pathname = usePathname();
  const isActive =
    (type === 'subscription' && visibility !== 'locked') ||
    pathname === `/${safeUrl}` ||
    pathname.startsWith(`/${safeUrl}/`) ||
    (safeUrl === 'home' && pathname === '/');

  const lockedIcon =
    type === 'subscription' && visibility === 'locked' ? (
      <Icon id="shopping-icon" className="size-[24px]" decorative />
    ) : null;

  const topNavClasses = type === 'default' ? '!font-FoxOneAeroBold md:!font-FoxOneAeroBlack' : '';

  let content = <span className={cn('capitalize', topNavClasses)}>{display_name}</span>;

  if (view_type === ICON) {
    content = (
      <div className="flex flex-row items-center gap-x-[20px] py-[13px] md:block md:gap-y-0">
        {images?.logo ? (
          <Image
            className={cn('h-[23px] w-auto', {
              'h-[24px]': item_key === 'fox_nation',
              'h-[18px]': item_key === 'fox_one',
              'h-[28px]': item_key === 'big_ten',
            })}
            src={images.logo}
            alt={`${displayName} icon`}
            width={240}
            height={28}
          />
        ) : (
          content
        )}
      </div>
    );
  } else if (view_type === TEXT_SUBSCRIPTION) {
    content = (
      <span className="flex items-center justify-between">
        {display_name}
        <Icon id="shopping-icon" className="ml-1 size-[28px]" decorative />
      </span>
    );
  }

  const dataAnalyticsStr = canonicalName;
  // Avoid hydration mismatch by not using client-only auth on first paint
  const defaultHref = `/${safeUrl}`;
  const href = !isAuthenticated && safeUrl === 'home' ? '/' : defaultHref;
  const prefetch = !isAuthenticated;

  return (
    <li className={cn('flex w-full items-center justify-between md:block md:w-fit', !isLast && 'pr-[35px]', className)}>
      <Link
        prefetch={prefetch}
        data-analytics={`${dataAnalyticsStr}-topnav-select`}
        href={lockedIcon ? `/mlp/${safeUrl}` : href}
        className={cn(
          'ml-[53px] whitespace-nowrap py-4 font-FoxOneAeroBold text-[17px]/[22px] font-medium text-base-white opacity-65 hover:opacity-100 md:ml-0 md:py-0 md:text-sm',
          {
            'text-base-white !opacity-100': isActive,
            'w-full items-center justify-between pr-[53px] opacity-50': view_type === TEXT_SUBSCRIPTION,
          },
        )}
      >
        {content}
      </Link>
      {lockedIcon}
    </li>
  );
}
